import { KB_MOTION, KB_LIFT_TRANSITION } from '../../components/common/keyboardOverlayStyles';
import { withoutHeightTransitions } from './modalMeasure';
import { flipSlide } from './modalAnticipationSlide';

/**
 * Step-bar tuck for `useModalKeyboardAnticipation` (KAN-552 1e). Plain DOM.
 *
 * A wizard card's own chrome above the body (title row + step bar) takes ~120pt;
 * with a low field focused iOS still shoves the app ~30pt even after the lift.
 * While typing on phone/tablet an OPTED-IN step bar (`[data-cvx-kb-tuck]`, see
 * `WizardStepProgress kbTuck`) folds to 0 height — SYNCHRONOUSLY in the trusted
 * focusin, before iOS samples — and every element below it glides (deferred
 * FLIP by its OWN measured delta) instead of jumping. It unfolds when the
 * keyboard closes, together with the card's pin tween (ONE beat). Timers,
 * never transitionend (iOS drops end events).
 */

const MOTION_MS = 250; // the UNTUCK tweens (KB_MOTION) — the tuck-down flips run on KB_LIFT_MS and clear themselves
const MOTION_PAD_MS = 80;
/** No keyboard arrived within this long → restore (matches the height freeze). */
const NO_KEYBOARD_MS = 1200;
const PROPS = ['height', 'overflow', 'opacity', 'transition', 'marginTop', 'marginBottom'];

function prefersReducedMotion() {
  const mq = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)');
  return !!(mq && mq.matches);
}

/** Following element siblings of the bar (+ the body scroller when it is not inside one). */
function followers(bar, scroller) {
  const list = [];
  for (let n = bar.nextElementSibling; n; n = n.nextElementSibling) list.push(n);
  if (scroller && !scroller.contains(bar) && !list.some((n) => n.contains(scroller))) list.push(scroller);
  return list;
}

export function createStepTuck(dialogEl, { isKeyboardOpen, isPinned, onRotate } = {}) {
  let el = null;
  let saved = null;
  let barH = 0;
  let tucked = false;
  let flips = [];
  let restoreTimer = null;
  let noKbTimer = null;
  // The card's own pin (when the freeze is not already pinning it): the card
  // never changes height at the tap — only its body grows into the freed space.
  let pin = null; // { height }
  let tuckWidth = 0;
  // Positions captured by settle() BEFORE it snaps the bar, reused by the very next tuck().
  let pending = null;
  let listening = false;

  const cancelFlips = () => { flips.forEach((c) => c()); flips = []; };
  const freezeOwnsHeight = () => !!(isPinned && isPinned());

  const dropPin = () => {
    if (!pin) return;
    const p = pin;
    pin = null;
    // Never overwrite a height the freeze now owns.
    if (freezeOwnsHeight()) return;
    dialogEl.style.height = p.height;
  };

  const restoreSaved = () => {
    if (el && saved) PROPS.forEach((p) => { el.style[p] = saved[p]; });
  };
  const clearTimers = () => {
    clearTimeout(restoreTimer);
    clearTimeout(noKbTimer);
    restoreTimer = null;
    noKbTimer = null;
  };

  // Own width-keyed rotation listener while we hold the bar or the pin (same
  // rule as the freeze: iOS fires `resize` when the keyboard opens — only a
  // WIDTH change is a rotation). Restores instantly and clears the lift.
  const onResize = () => {
    if (window.innerWidth === tuckWidth) return;
    reset();
    if (onRotate) onRotate();
  };
  const attach = () => {
    if (listening) return;
    listening = true;
    tuckWidth = window.innerWidth;
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
  };
  const detach = () => {
    if (!listening) return;
    listening = false;
    window.removeEventListener('resize', onResize);
    window.removeEventListener('orientationchange', onResize);
  };

  function reset() {
    clearTimers();
    restoreSaved();
    dropPin();
    cancelFlips();
    detach();
    tucked = false;
    el = null;
    saved = null;
  }

  const tuck = (scroller) => {
    if (!dialogEl || tucked || !scroller) return false;
    const bar = dialogEl.querySelector('[data-cvx-kb-tuck]');
    if (!bar) return false;
    let below = followers(bar, scroller);
    // Settled FIRST (a mid-flight flip's transform would skew the reads), then
    // the "before" tops — taken even mid-unfold, so a re-tuck FLIPs by the
    // bar's CURRENT height rather than the full freed amount. After a settle()
    // the positions it captured before snapping are reused.
    let before;
    if (pending) {
      ({ below, before } = pending);
      pending = null;
    } else {
      cancelFlips();
      before = below.map((n) => n.getBoundingClientRect().top);
    }
    const hadPin = !!pin;
    const curH = dialogEl.offsetHeight;
    if (el || pin) reset(); // mid-unfold: finish it, then tuck from the true resting state
    if (!freezeOwnsHeight()) {
      pin = { height: dialogEl.style.height };
      // Re-tuck mid-unfold keeps the card at the height it visibly has now.
      dialogEl.style.height = `${hadPin ? curH : dialogEl.offsetHeight}px`;
    }
    el = bar;
    saved = {};
    PROPS.forEach((p) => { saved[p] = bar.style[p]; });
    barH = bar.offsetHeight;
    bar.style.transition = 'none';
    bar.style.overflow = 'hidden';
    bar.style.height = '0px';
    bar.style.opacity = '0';
    bar.style.marginTop = '0px';
    bar.style.marginBottom = '0px';
    tucked = true;
    attach();
    // Each follower glides by its OWN delta; a 0px-tall band (or one that did
    // not move) takes no transform.
    below.forEach((n, i) => {
      const r = n.getBoundingClientRect();
      const delta = before[i] - r.top;
      if (r.height > 0 && delta > 0) flips.push(flipSlide(n, delta, { transition: KB_LIFT_TRANSITION }));
    });
    clearTimeout(noKbTimer);
    noKbTimer = setTimeout(() => {
      noKbTimer = null;
      if (tucked && !(isKeyboardOpen && isKeyboardOpen())) untuck({ animate: true });
    }, NO_KEYBOARD_MS);
    return true;
  };

  function untuck({ animate = true } = {}) {
    if (!el || !tucked) return;
    tucked = false;
    clearTimeout(noKbTimer);
    noKbTimer = null;
    if (!animate || prefersReducedMotion()) { reset(); return; }
    const bar = el;
    // The card's natural height: bar at its final size AND the pin released —
    // one synchronous pass, no paint. (The pinned offsetHeight is not it.)
    let natural = null;
    let pinnedPx = 0;
    if (pin && !freezeOwnsHeight()) {
      const cur = {};
      PROPS.forEach((p) => { cur[p] = bar.style[p]; });
      restoreSaved();
      const pinned = dialogEl.style.height;
      natural = withoutHeightTransitions(dialogEl, () => {
        dialogEl.style.height = pin.height;
        const n = dialogEl.offsetHeight;
        dialogEl.style.height = pinned;
        return n;
      });
      pinnedPx = parseFloat(pinned);
      PROPS.forEach((p) => { bar.style[p] = cur[p]; });
      // Folded again, transition still 'none': commit that layout so the
      // opacity/height tween below starts from the folded state.
      void bar.offsetHeight;
    }
    bar.style.transition = ['height', 'opacity', 'margin-top', 'margin-bottom']
      .map((p) => `${p} ${KB_MOTION}`).join(', ');
    void bar.offsetHeight; // force reflow so the tweens run from the folded state
    bar.style.height = `${barH}px`;
    bar.style.opacity = saved.opacity;
    bar.style.marginTop = saved.marginTop;
    bar.style.marginBottom = saved.marginBottom;
    if (natural != null && Math.abs(natural - pinnedPx) > 1) dialogEl.style.height = `${natural}px`;
    // ONE clock: reset() lands the inline values (and drops the pin) after the tweens.
    restoreTimer = setTimeout(reset, MOTION_MS + MOTION_PAD_MS);
  }

  return {
    tuck,
    untuck,
    /** Instant, unconditional restore (rotation, mid-unfold) — works whether or not `tucked`. */
    cancel() { pending = null; reset(); },
    /**
     * Finish an in-flight unfold instantly and drop OUR pin — called before the
     * height freeze takes over the card, so it never captures a stale px height.
     */
    settle(scroller) {
      if (tucked || !(el || pin)) return;
      if (el && scroller) {
        // Capture where content sits NOW (mid-unfold) so the next tuck()
        // FLIPs from there instead of from the snapped full-bar layout.
        cancelFlips();
        const below = followers(el, scroller);
        pending = { below, before: below.map((n) => n.getBoundingClientRect().top) };
        Promise.resolve().then(() => { pending = null; });
      }
      reset();
    },
    /** Hand our pin (prior inline height) to the freeze; null when we hold none. */
    takePin() { const p = pin; pin = null; return p; },
    /** The freeze declined the pin — keep it. */
    giveBackPin(p) { if (p && !pin) pin = p; },
    isTucked: () => tucked,
    /** The keyboard arrived: the no-keyboard fallback no longer applies. */
    onKeyboardOpen() { clearTimeout(noKbTimer); noKbTimer = null; },
    /**
     * Run `fn` with the bar at its FINAL (unfolded) size and our pin released,
     * then put the current state back — one synchronous pass, no paint. For
     * natural-height reads.
     */
    measureUnfolded(fn) {
      if (!el || !saved) return fn();
      const cur = {};
      PROPS.forEach((p) => { cur[p] = el.style[p]; });
      const pinned = dialogEl.style.height;
      const releasePin = !!pin && !freezeOwnsHeight();
      restoreSaved();
      if (releasePin) dialogEl.style.height = pin.height;
      return withoutHeightTransitions(dialogEl, () => {
        try { return fn(); } finally {
          PROPS.forEach((p) => { el.style[p] = cur[p]; });
          if (releasePin) dialogEl.style.height = pinned;
        }
      });
    },
    dispose() { pending = null; reset(); },
  };
}
